import React, { useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    toggleTimerManagerCollapse,
    setTimerManagerPosition,
    setTimerManagerDragging,
    hideTimerManager,
    resetTimerManagerHiddenState
} from '../../redux/Slices/timerSlice';
import { useRunningTimers, useTimerManager, useTimerSync } from '../../hooks/useTimer';
import TimerItem from './TimerItem';
import TimerActivities from './TimerActivities';
import './scss/TimerManager.scss';

// Icons
import DragIndicator from '../icons/DragIndicator';
import ExpandLess from '../icons/ExpandLess';
import ExpandMore from '../icons/ExpandMore';
import Close from '../icons/Close';
import Timer from '../icons/Timer';

const TimerManager = () => {
    const dispatch = useDispatch();
    const timerManagerRef = useRef(null);
    const dragHandleRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [selectedTimerForActivities, setSelectedTimerForActivities] = useState(null);

    const runningTimers = useRunningTimers();
    const { isVisible, isCollapsed, position, hasBeenManuallyHidden } = useTimerManager();

    // Use timer sync to keep running timers updated
    useTimerSync();

    // Reset manual hidden state when no timers are running
    useEffect(() => {
        if (runningTimers.length === 0 && hasBeenManuallyHidden) {
            dispatch(resetTimerManagerHiddenState());
        }
    }, [runningTimers.length, hasBeenManuallyHidden, dispatch]);

    // Handle mouse down on drag handle
    const handleMouseDown = (e) => {
        if (!dragHandleRef.current?.contains(e.target)) return;
        
        e.preventDefault();
        setIsDragging(true);
        dispatch(setTimerManagerDragging(true));

        const rect = timerManagerRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
    };

    // Handle mouse move for dragging
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;

            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;

            // Constrain to viewport
            const maxX = window.innerWidth - 320; // Timer manager width
            const maxY = window.innerHeight - 100; // Minimum height
            
            const constrainedX = Math.max(0, Math.min(newX, maxX));
            const constrainedY = Math.max(0, Math.min(newY, maxY));

            dispatch(setTimerManagerPosition({ x: constrainedX, y: constrainedY }));
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            dispatch(setTimerManagerDragging(false));
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset, dispatch]);

    const handleToggleCollapse = () => {
        dispatch(toggleTimerManagerCollapse());
    };

    const handleClose = () => {
        dispatch(hideTimerManager());
    };

    const handleShowActivities = (cardId) => {
        setSelectedTimerForActivities(cardId);
    };

    const handleBackToTimers = () => {
        setSelectedTimerForActivities(null);
    };

    // Don't render if not visible or no running timers
    if (!isVisible || runningTimers.length === 0) {
        return null;
    }


    return (
        <div
            ref={timerManagerRef}
            className={`timer-manager ${isCollapsed ? 'collapsed' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                left: position.x,
                top: position.y,
            }}
        >
            {/* Header */}
            <div className="timer-manager-header" ref={dragHandleRef} onMouseDown={handleMouseDown}>
                <div className="timer-manager-title">
                    <DragIndicator size={16} color="var(--primary-text-color)" />
                    <Timer size={18} color="var(--primary-text-color)" />
                    <span>Running Timers ({runningTimers.length})</span>
                </div>
                <div className="timer-manager-controls">
                    <button
                        className="timer-manager-collapse-btn"
                        onClick={handleToggleCollapse}
                        title={isCollapsed ? 'Expand' : 'Collapse'}
                    >
                        {isCollapsed ? (
                            <ExpandMore size={20} color="var(--primary-text-color)" />
                        ) : (
                            <ExpandLess size={20} color="var(--primary-text-color)" />
                        )}
                    </button>
                    <button
                        className="timer-manager-close-btn"
                        onClick={handleClose}
                        title="Close"
                    >
                        <Close size={16} color="var(--primary-text-color)" />
                    </button>
                </div>
            </div>

            {/* Content */}
            {!isCollapsed && (
                <div className="timer-manager-content">
                    {selectedTimerForActivities ? (
                        <TimerActivities
                            cardId={selectedTimerForActivities}
                            onBack={handleBackToTimers}
                        />
                    ) : (
                        <div className="timer-list">
                            {runningTimers.length === 0 ? (
                                <div className="no-timers">
                                    <Timer size={24} color="var(--primary-text-color)" />
                                    <p>No running timers</p>
                                </div>
                            ) : (
                                runningTimers.map((timer) => (
                                    <TimerItem
                                        key={timer.cardId}
                                        cardId={timer.cardId}
                                        timer={timer}
                                        onShowActivities={handleShowActivities}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TimerManager;
